import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { BlogPost } from '@/types/blog';
import { generateCompleteBlogPost, generateBlogCoverImage } from '@/utils/blogGenerator';
import { Wand, MessageSquare, Image, ArrowRight, Loader2, RefreshCw, Check } from 'lucide-react';
import { WizardContentRefinement } from './WizardContentRefinement';
import { WizardImageSelector } from './WizardImageSelector';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

interface WizardBlogGeneratorProps {
  onBlogGenerated: (blogPost: Partial<BlogPost>) => void;
}

type WizardStep = 'topic' | 'content' | 'images' | 'review';

const formSchema = z.object({
  topic: z.string().min(5, {
    message: "Topic must be at least 5 characters.",
  }),
  imageSource: z.enum(['huggingface', 'pexels']),
  tone: z.enum(['professional', 'conversational', 'technical'])
});

export const WizardBlogGenerator: React.FC<WizardBlogGeneratorProps> = ({ onBlogGenerated }) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('topic');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<Partial<BlogPost> | null>(null);
  const [imageOptions, setImageOptions] = useState<Array<{ url: string, attribution?: { photographer: string, photographerUrl?: string } }>>([]);
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      topic: '',
      imageSource: 'pexels',
      tone: 'professional'
    },
  });

  const handleInitialGenerate = async (values: z.infer<typeof formSchema>) => {
    if (!values.topic.trim()) {
      toast({
        title: "Topic Required",
        description: "Please enter a blog topic",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);

    try {
      toast({
        title: "Generating Blog Draft",
        description: `This may take a minute or two while we create initial content...`,
      });

      // Include tone in the prompt
      const toneAdjustedTopic = `Create a ${values.tone} blog post about: ${values.topic}`;
      const generatedPost = await generateCompleteBlogPost(toneAdjustedTopic, values.imageSource);
      setGeneratedContent(generatedPost);
      
      // Generate multiple image options for manual selection
      await generateImageOptions(values.topic, values.imageSource, 3);
      
      toast({
        title: "Draft Generated",
        description: "Your initial draft is ready. Now you can refine the content and select images.",
      });
      
      setCurrentStep('content');
    } catch (error) {
      console.error("Error generating blog:", error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate blog post",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateImageOptions = async (topic: string, source: 'huggingface' | 'pexels', count: number) => {
    try {
      const options = [];
      
      for (let i = 0; i < count; i++) {
        try {
          // Slightly vary the prompt for each image to get diverse results
          const promptVariation = i === 0 
            ? topic 
            : i === 1 
              ? `${topic} - alternative perspective` 
              : `${topic} - creative interpretation`;
              
          const imageResult = await generateBlogCoverImage(promptVariation, source);
          
          options.push({
            url: imageResult.imageUrl,
            attribution: {
              photographer: imageResult.photographer || "AI Generated",
              photographerUrl: imageResult.photographerUrl
            }
          });
        } catch (individualImageError) {
          console.error(`Error generating image ${i+1}/${count}:`, individualImageError);
          // Continue with the next image instead of failing the whole batch
        }
      }
      
      if (options.length === 0) {
        toast({
          title: "Image Generation Issue",
          description: "Could not generate any images. Please try a different image source or prompt.",
          variant: "destructive",
        });
      } else if (options.length < count) {
        // Some images were generated but not all
        toast({
          title: "Image Generation Partial Success",
          description: `Generated ${options.length} out of ${count} requested images.`,
          variant: "default",
        });
      }
      
      setImageOptions(options);
    } catch (error) {
      console.error("Error generating image options:", error);
      toast({
        title: "Image Generation Issue",
        description: "Could not generate all image options. You can try again or proceed with available images.",
        variant: "destructive",
      });
    }
  };

  const handleContentUpdate = (updatedContent: Partial<BlogPost>) => {
    setGeneratedContent(prev => prev ? { ...prev, ...updatedContent } : updatedContent);
    setCurrentStep('images');
  };

  const handleImageSelection = (selectedImageUrl: string, attribution?: { photographer: string, photographerUrl?: string }) => {
    setGeneratedContent(prev => prev ? { 
      ...prev, 
      coverImage: selectedImageUrl,
      imageAttribution: attribution
    } : { coverImage: selectedImageUrl, imageAttribution: attribution });
    setCurrentStep('review');
  };

  const handleSubmit = () => {
    if (generatedContent) {
      onBlogGenerated(generatedContent);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'topic':
        return (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleInitialGenerate)} className="space-y-4">
              <FormField
                control={form.control}
                name="topic"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Blog Topic</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter a blog topic, e.g., 'Best practices for securing Microsoft 365 for small businesses'"
                        className="h-32"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Be specific about what you want to cover in your blog post.
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tone"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Writing Tone</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-col space-y-1"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="professional" id="professional" />
                          <Label htmlFor="professional">Professional & Formal</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="conversational" id="conversational" />
                          <Label htmlFor="conversational">Conversational & Approachable</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="technical" id="technical" />
                          <Label htmlFor="technical">Technical & Detailed</Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="imageSource"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Image Source</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-col space-y-1"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="pexels" id="pexels" />
                          <Label htmlFor="pexels">Stock Photos (Pexels)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="huggingface" id="huggingface" />
                          <Label htmlFor="huggingface">AI Generated (HuggingFace)</Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              <Button 
                type="submit" 
                disabled={isGenerating || !form.getValues().topic.trim()} 
                className="bg-edgefi-purple hover:bg-purple-700 w-full"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating Initial Draft...
                  </>
                ) : (
                  <>
                    <Wand className="mr-2 h-4 w-4" />
                    Generate Draft
                  </>
                )}
              </Button>
            </form>
          </Form>
        );
        
      case 'content':
        return generatedContent ? (
          <WizardContentRefinement 
            initialContent={generatedContent} 
            onContentUpdated={handleContentUpdate} 
          />
        ) : (
          <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-edgefi-purple" />
          </div>
        );
        
      case 'images':
        return (
          <WizardImageSelector 
            imageOptions={imageOptions} 
            topic={form.getValues().topic}
            imageSource={form.getValues().imageSource}
            onSelectImage={handleImageSelection}
            onGenerateMore={() => generateImageOptions(form.getValues().topic, form.getValues().imageSource, 3)}
          />
        );
        
      case 'review':
        return generatedContent ? (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="mt-2 text-lg font-medium">Blog Post Ready!</h3>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium">Title</h3>
              <p className="text-lg font-bold">{generatedContent.title}</p>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium">Excerpt</h3>
              <p>{generatedContent.excerpt}</p>
            </div>
            
            {generatedContent.coverImage && (
              <div className="space-y-2">
                <h3 className="font-medium">Cover Image</h3>
                <div className="relative aspect-video overflow-hidden rounded-md border">
                  <img 
                    src={generatedContent.coverImage} 
                    alt={generatedContent.title} 
                    className="object-cover w-full h-full"
                  />
                  {generatedContent.imageAttribution && (
                    <div className="absolute bottom-0 right-0 bg-black/50 text-white text-xs px-2 py-1">
                      Photo by {generatedContent.imageAttribution.photographer}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <h3 className="font-medium">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {generatedContent.tags?.map((tag, index) => (
                  <span key={index} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-md">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="pt-2">
              <Button 
                onClick={handleSubmit} 
                className="w-full bg-edgefi-purple hover:bg-purple-700"
              >
                Add to Blog Editor
              </Button>
            </div>
            
            <div className="text-center text-sm text-gray-500 pt-2">
              You'll be able to make final edits in the blog editor
            </div>
          </div>
        ) : (
          <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-edgefi-purple" />
          </div>
        );
    }
  };

  const renderStepIndicator = () => {
    const steps = [
      { id: 'topic' as const, label: 'Topic & Style' },
      { id: 'content' as const, label: 'Content' },
      { id: 'images' as const, label: 'Images' },
      { id: 'review' as const, label: 'Review' }
    ];
    
    return (
      <div className="mb-6">
        <div className="flex justify-between items-center">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div 
                  className={`w-8 h-8 flex items-center justify-center rounded-full ${
                    currentStep === step.id 
                      ? 'bg-edgefi-purple text-white' 
                      : steps.findIndex(s => s.id === currentStep) > index 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {steps.findIndex(s => s.id === currentStep) > index ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span className="text-xs mt-1">{step.label}</span>
              </div>
              {index < steps.length - 1 && (
                <div 
                  className={`h-0.5 flex-1 mx-2 ${
                    steps.findIndex(s => s.id === currentStep) > index 
                      ? 'bg-green-500' 
                      : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wand className="h-5 w-5 text-edgefi-purple" />
          Blog Wizard
        </CardTitle>
      </CardHeader>
      <CardContent>
        {renderStepIndicator()}
        {renderStepContent()}
      </CardContent>
    </Card>
  );
};
